import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
} from '@angular/core';
import {
  CONTRIBUTED_TASKS_COMPONENT_IMPORTS,
  CONTRIBUTED_TASKS_INSIGHTS,
  DEFAULT_PAGE_SIZE_OPTIONS,
  DestroyRef,
  EMPLOYEE_ROUTES,
  ExportService,
  FeatureFlagsService,
  FEATURE_FLAGS,
  FILTER_OPTIONS,
  FormGroup,
  ModalServiceService,
  MyInboxService,
  ON_HOLD_COLUMNS,
  RequestTypeEnum,
  Router,
  StepStatusEnum,
  TABLE_FILTER_TABS,
  TenantService,
  ToasterService,
  TranslocoService,
  VIEW_DETAILS_ACTION,
  COLUMN_IDENTIFIERS,
  isFeatureFlaggedColumnVisible,
  finalize,
  inject,
  signal,
  takeUntilDestroyed,
  type ActionList,
  type ApiResponse,
  type CurrentAssignee,
  type FilterOption,
  type StepTask,
  type TableColumns,
  type TableFilterTab,
  type Insight,
  FilterMapping,
} from './contributed-tasks.imports';
import {
  DEFAULT_PAGE_SIZE,
  FilteredTablePayload,
  Meta,
} from '@kuiper/ibusiness-core';
import { mergeRequestSummaryColumns } from '../../../../common/helpers/request-summary-column/request-summary-column.helper';
import { mergeSlaSummaryColumns } from '../../../../common/helpers/sla-summary-column/sla-summary-column.helper';

@Component({
  selector: 'lib-contributed-tasks',
  imports: CONTRIBUTED_TASKS_COMPONENT_IMPORTS,
  templateUrl: './contributed-tasks.component.html',
  styleUrl: './contributed-tasks.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ContributedTasksComponent implements OnInit {
  readonly pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS;
  readonly EMPLOYEE_ROUTES = EMPLOYEE_ROUTES;
  protected readonly COLUMN_IDENTIFIERS = COLUMN_IDENTIFIERS;
  protected readonly stepStatusEnum = StepStatusEnum;

  private readonly myInboxService = inject(MyInboxService);
  private readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);
  private readonly modalService = inject(ModalServiceService);
  private readonly toasterService = inject(ToasterService);
  protected readonly exportService = inject(ExportService);
  private readonly tenantService = inject(TenantService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly featureFlagService = inject(FeatureFlagsService);
  /** UI_Version_2: ID, workflow type and subject share one Request column. */
  protected readonly mergeRequestColumns = signal(
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.TABLE.ui_enhancement
    )
  );

  protected readonly showParentTasksColumn = signal(
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.INBOX.parent_wf_column
    )
  );

  protected readonly showRelatedRequestsColumn = signal(
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOWS.parent_and_child_approach
    )
  );

  /******** pagination ********/
  meta = signal<Meta | undefined>(undefined);

  /*********** table  *********/
  isLoading = signal<boolean>(false);
  dataSource = signal<StepTask[]>([]);
  totalItems = signal<number>(0);
  readonly defaultColumns = computed<TableColumns[]>(() =>
    mergeSlaSummaryColumns(
      mergeRequestSummaryColumns(ON_HOLD_COLUMNS, this.mergeRequestColumns()),
      this.mergeRequestColumns()
    )
      .filter(col =>
        isFeatureFlaggedColumnVisible(col.header, {
          showParentTasksColumn: this.showParentTasksColumn(),
          showRelatedRequestsColumn: this.showRelatedRequestsColumn(),
        })
      )
      .map(column => {
        return {
          ...column,
          header: this.transloco.translate(column.header),
          viewIconRouterLink: column.viewIconRouterLink
            ? row => column.viewIconRouterLink(row, this.tenantService)
            : undefined,
        };
      })
  );
  columns = signal<TableColumns[]>(this.defaultColumns());
  tableFilterTabs = signal<TableFilterTab[]>(
    TABLE_FILTER_TABS.map(tab => ({
      ...tab,
      label: this.transloco.translate(tab.label),
    }))
  );
  payload = signal<FilteredTablePayload>({
    page: 1,
    size: DEFAULT_PAGE_SIZE,
    sortColumn: {
      key: '',
      value: 'asc',
    },
    search: '',
    filters: {},
    tab: this.tableFilterTabs()[0],
  });

  viewDetailsAction: ActionList = {
    ...VIEW_DETAILS_ACTION,
    label: this.transloco.translate(VIEW_DETAILS_ACTION.label),
  };

  filterOptions: FilterOption[] = FILTER_OPTIONS.map(option => ({
    ...option,
    label: this.transloco.translate(option.label),
    placeholder: option.placeholder
      ? this.transloco.translate(option.placeholder)
      : '',
  }));

  filterMapping: Record<string, string> = {
    workflowTypes: FilterMapping.WORKFLOW_TYPES,
    creationDateStart: FilterMapping.CREATED_AFTER,
    creationDateEnd: FilterMapping.CREATED_BEFORE,
    projectIds: FilterMapping.HAS_FLOW_PROJECT_IDS,
  };

  insights = signal<Insight[]>(CONTRIBUTED_TASKS_INSIGHTS);

  ngOnInit(): void {
    this.getInsights();
  }

  getInsights() {
    this.myInboxService.getContributedTasksInsight().subscribe({
      next: res => this.updateInsights(res),
    });
  }

  private updateInsights(res: ApiResponse<Insight>) {
    const keys = ['total_in_progress_tasks', 'total_on_hold_tasks'];
    this.insights.update(insights => {
      return insights.map((insight, index) => {
        const key = keys[index];
        const resInsight = res.data.find((i: Insight) => i.key === key);
        if (resInsight) {
          insight.value = resInsight.value?.toString() ?? '0';
        }
        return insight;
      });
    });
  }

  getContributedTasks$ = () => {
    this.isLoading.set(true);
    this.meta.set(undefined);

    const { page, size, tab, sortColumn, search, filters } = this.payload();

    return this.myInboxService
      .getStepsContributedTasks(
        page,
        size,
        tab?.key,
        sortColumn,
        search,
        filters
      )
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      );
  };

  getContributedTasks() {
    this.getContributedTasks$().subscribe({
      next: (res: ApiResponse<StepTask>) =>
        this.handleContributedTasksSuccess(res),
    });
  }

  handleContributedTasksSuccess(res: ApiResponse<StepTask>) {
    const data = res.data.map((element: StepTask) => ({
      ...element,
      current_assignees: element.assignees.map(
        (assignee: CurrentAssignee) => assignee.data
      ) as unknown as CurrentAssignee[],
      assigneeType: element.assignees.length
        ? element.assignees.map((assignee: CurrentAssignee) => assignee.type)
        : [],
    }));
    this.dataSource.set(data);
    this.meta.set(res.meta);
  }

  // Method to get router link for task details
  protected getRelatedRequestViewLink = (childFlow: { id: number }) =>
    this.tenantService.tenantPath(
      `${EMPLOYEE_ROUTES.MY_INBOX.REQUEST}/${RequestTypeEnum.VIEW}/${childFlow.id}`
    );

  getTaskRouterLink(taskId: number, flowId: number): string[] {
    return [
      this.tenantService.tenantPath(
        `${EMPLOYEE_ROUTES.MY_INBOX.LIST}/${taskId.toString()}/${RequestTypeEnum.CONTRIBUTE}/${flowId.toString()}`
      ),
    ];
  }

  viewTaskDetails(task: StepTask) {
    this.router.navigate(this.getTaskRouterLink(task.id, task.flow.id));
  }

  getViewActionList(taskId: number, flowId: number): ActionList[] {
    this.viewDetailsAction.routerLinks = [
      this.tenantService.tenantPath(
        `${EMPLOYEE_ROUTES.MY_INBOX.LIST}/${taskId.toString()}/${RequestTypeEnum.CONTRIBUTE}/${flowId.toString()}`
      ),
    ];
    return [this.viewDetailsAction];
  }

  actionListItemClick(action: string, row: StepTask) {
    if (action === 'view') {
      this.router.navigate([
        this.tenantService.tenantPath(
          `${EMPLOYEE_ROUTES.MY_INBOX.LIST}/${row.id.toString()}/${RequestTypeEnum.CONTRIBUTE}/${row.flow.id.toString()}`
        ),
      ]);
    }
  }

  closeModal() {
    this.modalService.modalContent.set({ isOpen: false });
  }

  submitExportForm(form: FormGroup) {
    const formValue = form.value;
    this.myInboxService
      .exportAssignedToMe(formValue)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: unknown) => {
          this.exportService.handleExportSuccess(
            response as Response,
            form,
            () => this.closeModal()
          );
        },
        error: (error: { error?: { message?: string } }) => {
          this.toasterService.add(
            error.error?.message ?? '',
            '',
            '',
            'error',
            true,
            true
          );
          this.exportService.reInitExportForm(form);
          this.closeModal();
        },
      });
  }
}
